













































































import { Vue, Component } from 'vue-property-decorator'
import { StateChanger } from 'vue-infinite-loading'
import { ExStore } from 'vuex'
import { Patient } from '@/types/user'
import dayjs from 'dayjs'
import { isCordovaEnvironment } from '@/utils/cordova'
import { isCapacitorEnvironment } from '@/utils/capacitor'

interface HTMLAnchorElement {
  download: string
}

@Component({
  components: {
    ImageList: () => import('@/components/imageTab/ImageList.vue'),
    SyncImage: () => import('@/components/imageTab/SyncImage.vue'),
    ImageModal: () => import('@/components/imageTab/ImageModal.vue')
  }
})
export default class ImageIndex extends Vue {
  $store!: ExStore
  thumbnailUrls: {
    [id: number]: string
  } = {}
  selectedImage: number | null = null
  selectedImageUrl = ''
  patient: Patient | null = null

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get patientName() {
    if (!this.patient) return ''
    return `${this.patient.lastName}${this.patient.firstName}`
  }

  get patientId() {
    return Number(this.$route.params.patientId)
  }

  get images() {
    return this.$store.getters['image/images']
  }

  get hasImageNext() {
    return this.$store.getters['image/hasImageNext']
  }

  get importantImages() {
    return this.$store.getters['image/importantImages']
  }

  get hasImportantImageNext() {
    return this.$store.getters['image/hasImportantImageNext']
  }

  get selectedImageInfo() {
    const image = this.getImageFile(this.selectedImage)
    if (!image) return {}
    const senderName = image.worker
      ? `${image.worker.lastName}${image.worker.occupation.name}`
      : this.patientName
    return {
      id: image.id,
      senderName,
      date: dayjs(image.registeredDatetime).format('M月D日 HH:mm'),
      description: image.description
    }
  }

  async created() {
    await this.fetchPatient()

    this.$store.dispatch('image/updateImages', { patientId: this.patientId })
    this.$store.dispatch('image/updateImportantImages', {
      patientId: this.patientId
    })
  }

  beforeDestroy() {
    for (const key in this.thumbnailUrls) {
      const url = this.thumbnailUrls[key]
      URL.revokeObjectURL(url)
    }
    URL.revokeObjectURL(this.selectedImageUrl)
  }

  async fetchPatient() {
    this.patient = await this.$api.user.patient({ patientId: this.patientId })
  }

  getImageFile(id: number | null) {
    if (!id) return
    return (
      this.images.find(img => img.id === id) ||
      this.importantImages.find(img => img.id === id)
    )
  }

  handleEdit(imageId: number) {
    this.$router.push({
      name: 'ImageTabEdit',
      params: { imageId: String(imageId) }
    })
  }

  async handleDownload(imageId: number) {
    const image = this.getImageFile(imageId)
    if (!image) throw new Error('does not exist image file')
    if (isCordovaEnvironment() || isCapacitorEnvironment()) {
      this.$postMessage.openFileDownload({
        isPatient: this.userInfo.isPatient,
        isRelative: this.userInfo.isPatientRelative,
        type: 'image',
        patientId: this.patientId,
        fileId: imageId,
        fileName: image.fileName || ''
      })
      return
    }
    const res = await this.$api.image.fetchImage({
      patientId: this.patientId,
      imageId
    })
    const url = window.URL.createObjectURL(res)
    const a = document.createElement('a')
    a.style.opacity = '0'
    document.body.appendChild(a)
    a.download = image.fileName || ''
    a.href = url
    a.click()
    a.remove()
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 100)
  }

  async handleDelete(imageId: number) {
    await this.$store.dispatch('image/removeImage', {
      patientId: this.patientId,
      imageId
    })
  }

  async handleUpdateImportantStatus({
    imageId,
    isImportant
  }: {
    imageId: number
    isImportant: boolean
  }) {
    await this.$store.dispatch('image/updateImportantStatus', {
      patientId: this.patientId,
      isImportant,
      imageId
    })
  }

  getThumbnailUrl(imageId: number) {
    return this.thumbnailUrls[imageId] || ''
  }

  handleCloseModal() {
    this.selectedImage = null
    this.selectedImageUrl = ''
  }

  handleSelectImage(imageId: number) {
    this.selectedImage = imageId
  }

  handleLoadNextImage($state: StateChanger) {
    this.$store
      .dispatch('image/loadNextImages', { patientId: this.patientId })
      .then(() => {
        if (this.hasImageNext) {
          $state.loaded()
        } else {
          $state.complete()
        }
      })
  }

  handleLoadNextImportantImage($state: StateChanger) {
    this.$store
      .dispatch('image/loadNextImportantImages', { patientId: this.patientId })
      .then(() => {
        if (this.hasImportantImageNext) {
          $state.loaded()
        } else {
          $state.complete()
        }
      })
  }

  async handleThumbnailCreated(imageId: number) {
    if (imageId in this.thumbnailUrls) return
    const res = await this.$api.image.fetchThumbnail({
      patientId: this.patientId,
      imageId
    })
    const url = window.URL.createObjectURL(res)
    this.thumbnailUrls = { ...this.thumbnailUrls, [imageId]: url }
  }

  async handleImageCreated(imageId: number) {
    const res = await this.$api.image.fetchImage({
      patientId: this.patientId,
      imageId
    })
    const url = window.URL.createObjectURL(res)
    this.selectedImageUrl = url
  }
}
